import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import UserService from "../services/userService";
import LoadindForButton from '../components/LoadingForButton';
import { Link } from 'react-router-dom';
import { FaEnvelope } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import AuthSideVisual from '../components/AuthSideVisual';
import ServerStatusNotice from '../components/ServerStatusNotice';

const getInitialTimer = () => {
    const start = localStorage.getItem('forgot_password_timer_start');
    if (!start) return 0;
    const elapsed = Math.floor((Date.now() - parseInt(start, 10)) / 1000);
    const left = 60 - elapsed;
    return left > 0 ? left : 0;
};

const ForgotPasswordEmail = ({ onEmailSubmit }) => {
    const { t } = useTranslation();

    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(getInitialTimer);
    const [invalidCredentials, setInvalidCredentials] = useState(false);
    const serverStatus = useSelector((state) => state.serverStore.status);

    useEffect(() => {
        if (resendTimer <= 0) return;
        const interval = setInterval(() => {
            const start = localStorage.getItem('forgot_password_timer_start');
            if (!start) return;
            const elapsed = Math.floor((Date.now() - parseInt(start, 10)) / 1000);
            const left = 60 - elapsed;
            if (left <= 0) {
                setResendTimer(0);
                localStorage.removeItem('forgot_password_timer_start');
                clearInterval(interval);
            } else {
                setResendTimer(left);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [resendTimer > 0]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

        if (!email) {
            setError(t('emailRequired'));
            return;
        } else if (!emailRegex.test(email) || email.endsWith('.')) {
            setError(t('invalidEmailFormat'));
            return;
        }

        setLoading(true);
        setError("");

        UserService.forgotPassword({ email })
            .then((res) => {
                if (res.status === 200) {
                    onEmailSubmit();
                    localStorage.setItem('forgot_password_timer_start', Date.now().toString());
                    setResendTimer(60);
                } else {
                    setInvalidCredentials(true);
                }
            })
            .catch(() => {
                setInvalidCredentials(true);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <div className="py-7 lg:px-4 flex flex-col lg:flex-row items-center justify-center h-full">
            <div className="w-full lg:w-1/2 max-w-screen-md mx-auto">
                <div className="flex justify-center">
                    <img src="/logo2.png" className="w-12 h-12 lg:w-32 lg:h-32" alt="Weddinfy" />
                </div>
                <form onSubmit={handleSubmit} className="p-4 w-full max-w-screen-md">
                    <h2 className="text-3xl font-bold my-2">{t('forgotPasswordTitle')}</h2>
                    <span className="block mb-4">{t('forgotPasswordSubtitle')}</span>
                    <div className="relative mt-4">
                        <label>{t('email')}</label>
                        <FaEnvelope className='inputWeddingIcon' />
                        <input
                            type="email"
                            name="email"
                            maxLength={50}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={`inputWedding ${error ? '!border-red-500' : 'inputFieldNormal'}`}
                        />
                    </div>

                    {error && <p className="text-red-500 text-sm">{error}</p>}

                    {invalidCredentials && <p className="text-red-500 text-sm">{t('invalidCredentials')}</p>}

                    <ServerStatusNotice />

                    <button type="submit" className="submitButton w-full mt-7" disabled={loading || resendTimer > 0 || serverStatus !== 'ready'}>
                        {loading || serverStatus === 'checking' ? <LoadindForButton /> : resendTimer > 0 ? t('resendIn', { seconds: resendTimer }) : t('sendCode')}
                    </button>

                    <div className="text-center mt-7">
                        <p className="text-gray-700">
                            {t('backToLogin')} <Link to="/login" className="text-gray-800 font-semibold underline">{t('login')}</Link>
                        </p>
                    </div>
                </form>
            </div>

            <div className="hidden lg:flex w-1/2 flex-col items-center justify-center text-center px-8 py-12 space-y-6">
                {/* <div className="w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center">
                    <FaChair className="text-white text-4xl" />
                </div>
                <Link to="/">
                    <h1 className="text-3xl font-extrabold text-gray-800">{t('appName')}</h1>
                </Link> */}
                <AuthSideVisual />
                <p className="text-gray-700 max-w-md">{t('slogan')}</p>
            </div>
        </div>
    );
};

export default ForgotPasswordEmail;
